// Ported from dev-scrips-pm-react src/billing/services/use-invoice-actions.ts (PROD-1119 cross-module closure;
// re-synced PROD-3312). PA keeps its print-epoch guard + store-owned blob URLs (PROD-1001) instead of pm-react's
// 60s revoke timer; every endpoint, body and invalidation below matches pm-react.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import { resolvePdfResponseToUrl } from '@/shared/lib/pdf-response';
import { usePrintPreviewStore } from '@/billing/store/print-preview-store';
import { bumpPrintEpoch, currentPrintEpoch } from '@/billing/services/print-epoch';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type { InvoiceDetail, ManagementDiscount } from '@/shared/types/billing';

/**
 * Print + Discount mutation hooks for the Invoice Detail surface.
 *
 * Wire endpoints (Flutter `billing_data_source_impl.dart:1752-1859`):
 *
 *   GET  /Billing/Invoice/PrintInvoice/{invoiceId}      → string URL
 *   GET  /Billing/Invoice/PrintReceipts/{receiptId}?invoiceId={id}
 *                                                       → string URL
 *   PUT  /Billing/Invoice/UpdateServiceDiscount?invoiceId={id}
 *        body { discountAmount, discountReason, invoiceDetailsId }
 *   POST /Billing/ManagementDiscount/Create body request
 *
 * Print endpoints return a quoted URL string (Flutter strips quotes via
 * `.replaceAll('"', '')`); we mirror that here. The resolved URL is handed to
 * the shared print-preview screen (`usePrintPreviewStore`) — Flutter shows the
 * PDF in an in-app preview (`PrintInvoiceState` / `PrintReceiptState`), NOT a
 * new browser tab (PROD-1001). The preview dialog itself offers Print +
 * "Open in new tab" as the actual print paths.
 */

/**
 * Print-epoch guard (PROD-1001, Codex v5): `useMutation` keeps running after
 * the component that started it (and AppShell) unmounts, so an in-flight
 * `usePrintInvoice`/`usePrintReceipt` request is not cancelled by a logout, a
 * newer print click, or an unmount. If a stale request's resolved PDF is
 * still handed to the (module-global, singleton) `usePrintPreviewStore`, it
 * can either replace a NEWER, still-in-flight print's result (wrong
 * patient's doc shown) or resurface a PRE-logout document after a
 * same-user re-auth. Callers capture `myEpoch` (via `currentPrintEpoch()`,
 * immediately after bumping it) right after the request starts; this
 * re-checks it right before `store.open()`. Any epoch mismatch — a newer
 * print started, a login/logout happened, or AppShell unmounted — discards
 * the result (revoking any owned blob so the PHI doesn't linger in memory)
 * instead of displaying it. See `print-epoch.ts` for the full mechanism.
 */
function openIfEpochCurrent(
  myEpoch: number,
  url: string,
  isBlob: boolean,
  opts: { title: string; autoPrint: boolean },
) {
  if (myEpoch !== currentPrintEpoch()) {
    if (isBlob) URL.revokeObjectURL(url);
    return;
  }
  usePrintPreviewStore.getState().open(url, { ...opts, ownsBlob: isBlob });
}

/** Fetch the raw invoice-print response (URL string OR base64 PDF bytes). */
export function fetchPrintInvoiceResponse(
  invoiceId: string,
  signal?: AbortSignal,
) {
  return get<string>(`${BILLING_BASE}/Invoice/PrintInvoice/${invoiceId}`, {
    signal,
  }).then((raw) => (typeof raw === 'string' ? raw : String(raw)));
}

/** Fetch the raw receipt-print response (URL string OR base64 PDF bytes). */
export function fetchPrintReceiptResponse(
  receiptId: string,
  invoiceId?: string,
  signal?: AbortSignal,
) {
  const qs = invoiceId ? `?invoiceId=${invoiceId}` : '';
  return get<string>(`${BILLING_BASE}/Invoice/PrintReceipts/${receiptId}${qs}`, {
    signal,
  }).then((raw) => (typeof raw === 'string' ? raw : String(raw)));
}

/**
 * Resolve the invoice-print response and show it in the in-app preview screen
 * (Flutter `PrintInvoiceState`). The backend returns either a SAS URL or base64
 * PDF bytes — `resolvePdfResponseToUrl` handles both (same defensive logic as
 * the PVS print, `use-post-visit-summary.ts`). A non-PDF / empty body throws,
 * surfaced by the global toast.
 *
 * When the response is base64, `resolvePdfResponseToUrl` mints a same-origin
 * `blob:` url; we hand ownership to the store (`ownsBlob`) so it is revoked when
 * the preview closes — NOT on a fixed timer that could invalidate "Open in new
 * tab" while the dialog is still open (PROD-1001 review, P2).
 */
export function usePrintInvoice() {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // Starting a new print supersedes any older in-flight one — bump FIRST,
      // then capture so this request owns the latest epoch (PROD-1001 Codex
      // v5 P1: two Print clicks before either resolves must not let the
      // earlier one win on late resolution).
      bumpPrintEpoch();
      const myEpoch = currentPrintEpoch();
      const raw = await fetchPrintInvoiceResponse(invoiceId);
      const { url, isBlob } = resolvePdfResponseToUrl(raw);
      openIfEpochCurrent(myEpoch, url, isBlob, {
        title: 'Invoice',
        autoPrint: true,
      });
      return url;
    },
  });
}

/**
 * Resolve a receipt-print response and show it in the preview screen (Flutter
 * `PrintReceiptState`). `autoPrint` distinguishes the two claim-popup
 * affordances: "Print" tries to print on open (reliable for base64 → blob URLs,
 * which are same-origin); "View Receipt" just previews.
 */
export function usePrintReceipt() {
  return useMutation({
    mutationFn: async ({
      receiptId,
      invoiceId,
      autoPrint = true,
    }: {
      receiptId: string;
      invoiceId?: string;
      /** true = "Print" (try to print on open); false = "View Receipt" (preview only). */
      autoPrint?: boolean;
    }) => {
      bumpPrintEpoch();
      const myEpoch = currentPrintEpoch();
      const raw = await fetchPrintReceiptResponse(receiptId, invoiceId);
      const { url, isBlob } = resolvePdfResponseToUrl(raw);
      openIfEpochCurrent(myEpoch, url, isBlob, {
        title: 'Receipt',
        autoPrint,
      });
      return url;
    },
  });
}

/**
 * Per-service discount edit. The backend receives an `invoiceDetailsId`
 * (the service line item, NOT the service master id) and either applies
 * or clears a discount line. Set `discountAmount` to `null` to remove.
 */
export interface UpdateServiceDiscountVars {
  invoiceId: string;
  invoiceDetailsId: string;
  discountAmount: number | null;
  discountReason?: string;
}

export function useUpdateServiceDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      invoiceDetailsId,
      discountAmount,
      discountReason,
    }: UpdateServiceDiscountVars) =>
      put<InvoiceDetail>(
        `${BILLING_BASE}/Invoice/UpdateServiceDiscount?invoiceId=${invoiceId}`,
        {
          invoiceDetailsId,
          discountAmount,
          discountReason: discountReason ?? '',
        },
      ),
    onSuccess: (_data, vars) => {
      showSuccess(
        vars.discountAmount == null
          ? 'Discount removed'
          : 'Discount applied',
      );
      void qc.invalidateQueries({
        queryKey: ['invoice', 'detail', vars.invoiceId],
      });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      invalidateBalanceViews(qc); // Total Unpaid / balances (PROD-1809)
    },
  });
}

/**
 * Invoice-wide management discount (Self-pay) — opened from clicking Patient
 * Payable in the totals box. Flutter `AddManagementDiscountRequestModel`
 * (`add_discount_management_request_model.dart`): the wire body is
 * `{ invoiceId, type, amount, reason }` where `type` is `'Amt'` (flat amount)
 * or `'Pct'` (percentage) — NOT `discountAmount`/`managedById`. The backend
 * allocates the discount across eligible services; response isn't the invoice,
 * so we just invalidate + refetch.
 */
export interface AddManagementDiscountVars {
  invoiceId: string;
  /** 'Amt' = flat amount · 'Pct' = percentage (Flutter toggle). */
  type: 'Amt' | 'Pct';
  amount: number;
  reason: string;
}

export function useAddManagementDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: AddManagementDiscountVars) =>
      post(`${BILLING_BASE}/ManagementDiscount/Create`, {
        invoiceId: vars.invoiceId,
        type: vars.type,
        amount: vars.amount,
        reason: vars.reason,
      }),
    onSuccess: (_data, vars) => {
      showSuccess('Management discount applied');
      void qc.invalidateQueries({
        queryKey: ['invoice', 'detail', vars.invoiceId],
      });
      void qc.invalidateQueries({ queryKey: ['payments', 'list', vars.invoiceId] });
      void qc.invalidateQueries({ queryKey: ['management-discounts', vars.invoiceId] });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      invalidateBalanceViews(qc); // Total Unpaid / balances (PROD-1809)
    },
  });
}

/**
 * Existing management discounts on an invoice — Flutter's
 * `add_management_discount_popup.dart` list. Backend:
 *   GET /api/Billing/ManagementDiscount/{invoiceId} -> ManagementDiscountDto[]
 * Feeds the dialog's existing-discount table so the user can see + remove
 * what's already applied (PROD-1751). Fetched only while the dialog is open.
 */
export function useManagementDiscounts(invoiceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['management-discounts', invoiceId],
    enabled: Boolean(invoiceId) && enabled,
    queryFn: ({ signal }) =>
      get<ManagementDiscount[]>(`${BILLING_BASE}/ManagementDiscount/${invoiceId}`, { signal }),
  });
}

/**
 * Remove a management discount — Flutter `DeleteDiscountEvent(id)`. Backend:
 *   PUT /api/Billing/ManagementDiscount/Delete  body { discountId }  (soft-delete)
 * Invalidates the invoice + its discount list + totals so Patient Payable and
 * the Mgmt. Discount row refresh (PROD-1751).
 */
export function useDeleteManagementDiscount(invoiceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (discountId: string) =>
      put(`${BILLING_BASE}/ManagementDiscount/Delete`, { discountId }),
    onSuccess: () => {
      showSuccess('Management discount removed');
      void qc.invalidateQueries({ queryKey: ['management-discounts', invoiceId] });
      void qc.invalidateQueries({ queryKey: ['invoice', 'detail', invoiceId] });
      void qc.invalidateQueries({ queryKey: ['payments', 'list', invoiceId] });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      invalidateBalanceViews(qc); // Total Unpaid / balances (PROD-1809)
    },
  });
}
